import { injectable, interfaces } from 'inversify';
import { action, IObservableArray, makeObservable, observable } from 'mobx';
import { IPagingInfoWithOrder } from '../../models/paginationInfo.model';
import { IDatasetDetails } from './datasetsDetails.store';
import { DatasetStatus } from './datasetStatus.model';

const PAGE_SIZE = 120;

const DatasetsDefaultPaging = { pageNumber: 1, pagesCount: 1, totalCount: 1, pageSize: PAGE_SIZE, orderBy: 'name', orderType: 'asc', search: '' };

export interface IDatasetListItemViewModel {
  id: string;
  workspaceId: string;
  name: string;
  description: string;
  author: string;
  termsOfUse: string;
  imagesCount: number;
  projectsCount: number;
  imagesSize: number;
  status: DatasetStatus;
  lastModified: string;
  createdDate: string;
  orderBy?: string;
  orderType?: string;
  createdBy: string;
  createdById: string;
}

export interface IDatasetsStore {
  readonly datasets: IObservableArray<IDatasetListItemViewModel>;
  readonly datasetsPaging: IPagingInfoWithOrder;
  readonly workspaceId: string;
  stale: boolean;

  setDatasets(datasets: IDatasetListItemViewModel[], workspaceId: string, paging: IPagingInfoWithOrder): void;
  markAsUploaded(datasetId: string): void;
  markAsUploading(datasetId: string): void;
  incrementProgress(datasetId: string, imageSize: number): void;
  refreshFromDetails(details: IDatasetDetails): void;
}

export namespace IDatasetsStore {
  export const $: interfaces.ServiceIdentifier<IDatasetsStore> = Symbol('DATASETS_STORE');
}

@injectable()
export class DatasetsStore implements IDatasetsStore {
  datasets: IObservableArray<IDatasetListItemViewModel> = observable.array([]);

  datasetsPaging: IPagingInfoWithOrder = DatasetsDefaultPaging;

  workspaceId: string = '';

  stale = false;

  constructor() {
    makeObservable(this, {
      datasets: observable,
      datasetsPaging: observable,
      workspaceId: observable,
      stale: observable,
      setDatasets: action,
      markAsUploaded: action,
      incrementProgress: action,
      markAsUploading: action,
      refreshFromDetails: action.bound,
    });
  }

  setDatasets(datasets: IDatasetListItemViewModel[], workspaceId: string, paging: IPagingInfoWithOrder): void {
    this.datasets.replace(datasets);
    this.workspaceId = workspaceId;
    this.datasetsPaging = paging;
    this.stale = false;
  }

  markAsUploaded(datasetId: string): void {
    const dataset = this.datasets.find((x) => x.id === datasetId);
    if (dataset === undefined) {
      return;
    }

    dataset.status = dataset.status === 'PublishedUploading' ? 'Published' : 'Draft';

    this.datasets.splice(
      this.datasets.findIndex((s) => s.id === datasetId),
      1,
      dataset
    );
  }

  incrementProgress(datasetId: string, imageSize: number): void {
    const dataset = this.datasets.find((x) => x.id === datasetId);
    if (dataset === undefined) {
      return;
    }
    dataset.imagesCount += 1;
    dataset.imagesSize += imageSize;
    this.datasets.splice(
      this.datasets.findIndex((s) => s.id === datasetId),
      1,
      dataset
    );
  }

  markAsUploading(datasetId: string): void {
    const dataset = this.datasets.find((x) => x.id === datasetId);
    if (dataset === undefined) {
      return;
    }

    dataset.status = dataset.status === 'Draft' ? 'Uploading' : 'PublishedUploading';

    this.datasets.splice(
      this.datasets.findIndex((s) => s.id === datasetId),
      1,
      dataset
    );
  }

  refreshFromDetails(details: IDatasetDetails): void {
    const datasetIndex = this.datasets.findIndex((x) => x.id === details.id);
    if (datasetIndex === -1) {
      this.datasets.push({ ...details });
    } else {
      this.datasets.splice(datasetIndex, 1, { ...details });
    }
    this.stale = false;
  }
}
